<template>
    <div v-if="singleOrMany=='single'">
        <Skeleton v-if="loader.pending.value" :items="['block', 'block--height-8rem', 'block', 'block--height-8rem', 'block--height-8rem', 'block', 'block--height-8rem', 'button']"/>
        <div
            v-else-if="bucketsSingleJobsite.length == 1">
            <DemandBrowserJobsiteItem :init-data="{'bucket': bucketsSingleJobsite[0], 'spotlight': spotlight}" />
        </div>
        <div v-else>
            <RichResponseHeadline v-if="spotlight=='dienstvolumen'" :title="((begin && until) ? formatRange(begin, until) : (begin ? ('ab ' + date.formatKapitelDate(begin)) : '')) + ' in den Schichten '  + shiftSelection.join(', ')" />

            <template
                v-if="spotlight=='stationsdetails'"
                v-for="bucket in bucketsSingleJobsite"
            >
                <JobsiteCompactRichResponseListItem
                    v-if="bucket.jobsite"
                    :jobsite="bucket.jobsite"
                    :demandBucket="bucket as DemandBucketSingleJobsite"
                    @click="jobsiteDetailModalModelValue = bucket.jobsite.id"
                />
            </template>
            <RichResponseListItemGroup
                v-else-if="spotlight=='dienstvolumen'"
                v-for="bucket in bucketsSingleJobsite"
            >
                <JobsiteCompactRichResponseListItem
                    v-if="bucket.jobsite"
                    :jobsite="bucket.jobsite"
                    :demandBucket="bucket as DemandBucketSingleJobsite"
                    @click="jobsiteDetailModalModelValue = bucket.jobsite.id"
                />
                <RichResponseListItem
                    iconType="text"
                    :icon="singleJobsiteBucketDienstvolumenRenderset(bucket).icon"
                    :title="singleJobsiteBucketDienstvolumenRenderset(bucket).title"
                    :button="true"
                    @click="() => onClickButtonMoreInfo(bucket)"
                >
                    {{singleJobsiteBucketDienstvolumenRenderset(bucket).subtitle}}
                </RichResponseListItem>
            </RichResponseListItemGroup>
        </div>

    </div>
    <div v-else-if="singleOrMany=='many' && bucketManyJobsites">
        <RichResponseHeadline :title="'Im Zeitraum '+ ((begin && until) ? formatRange(begin, until) : (begin ? ('ab ' + date.formatKapitelDate(begin)) : '')) + ' in den Schichten '  + shiftSelection.join(', ')" />
        <RichResponseListItem :iconType="undefined">
            <div>
                <ion-chip class="small" color="success">{{bucketManyJobsites.offeneDiensteStationen}}</ion-chip>Stationen
                <ion-chip class="small" color="tertiary">{{bucketManyJobsites.offeneDienstMenge}}</ion-chip>offene Dienste
            </div>
        </RichResponseListItem>
    </div>
    <div v-else>
        <Skeleton v-if="loader.pending.value" :items="['block--height-8rem', 'block--height-8rem', 'block--height-8rem']"/>
    </div>
    <JobsiteDemand
        v-model="jobsiteDemandModalModelValue"
        :shift-selection="shiftSelection"
        :startMonth="jobsiteDemandModalStartMonth"
    />
    <JobsiteDetails v-model="jobsiteDetailModalModelValue"/>

</template>

<script setup lang="ts">
import RichResponseListItem from "./components/RichResponseListItem.vue"
import RichResponseListItemGroup from "./components/RichResponseListItemGroup.vue"
import {computed, Ref, ref} from "vue"
import {DemandBucketManyJobsites, DemandBucketSingleJobsite, Jobsite, ShiftType} from "@/graphql/generated/graphql"
import {getDemandBuckets} from "@/helper/demand"
import {DataLoader} from "@/helper/dataLoader.js";
import DemandBrowserJobsiteItem from "@/views/Chat/RichResponses/DemandBrowserJobsiteItem.vue";
import {formatRange} from "@/helper/datetime/dateRange";
import {kapitelDateString, kapitelMonthString} from "@/graphql/kapitelTypes";
import date from "@/helper/datetime/date";
import RichResponseHeadline from "@/views/Chat/RichResponses/components/RichResponseHeadline.vue";
import {IonChip} from "@ionic/vue";
import JobsiteDemand from "@/views/JobsiteDemand.vue";
import JobsiteCompactRichResponseListItem from "@/views/JobsiteCompactRichResponseListItem.vue";
import Skeleton from "@/components/Skeleton.vue";
import JobsiteDetails from "@/views/JobsiteDetails.vue";
import {isSet} from "@/helper/isset";
import {pluralizedBookingAmount} from "@/helper/booking";
import {listFormat, pluralizeHuman, pluralizeUnitOnly} from "@/helper/amountFormatter";
import month from "@/helper/datetime/month";

const props = defineProps<{
    initData: any
}>()

const jobsiteIds: Ref<['*']|number[]|null> = ref([])
const buckets : Ref<(DemandBucketManyJobsites|DemandBucketSingleJobsite)[]> = ref([])
const spotlight : Ref<'stationsdetails'|'dienstvolumen'> = ref('dienstvolumen')
const begin: Ref<kapitelDateString|undefined> = ref(undefined)
const until: Ref<kapitelDateString|undefined> = ref(undefined)
const shiftSelection : Ref<ShiftType[]> = ref([])

const capitalize = (str: string):string => str.charAt(0).toUpperCase() + str.slice(1)

const singleOrMany : Ref<"single"|"many"|undefined> = computed(() => {
    if(Array.isArray(props.initData.jobsiteIds)){
        if(props.initData.jobsiteIds.length == 1){
            return "single"
        }
    }
    if (buckets.value.length == 0) {
        return undefined
    }
    if (buckets.value.length == 1 && !('jobsite' in buckets.value[0])) {
        return 'many'
    }
    return "single"
})
const bucketsSingleJobsite = computed(() => singleOrMany.value === 'single' ? buckets.value.map(b => b as DemandBucketSingleJobsite) : [])
const bucketManyJobsites = computed(() => singleOrMany.value === 'many' ? buckets.value[0] as DemandBucketManyJobsites : undefined)

const jobsiteDemandModalModelValue : Ref<Jobsite|undefined> = ref(undefined)
const jobsiteDemandModalStartMonth : Ref<kapitelMonthString|undefined> = ref(undefined)
const jobsiteDetailModalModelValue : Ref<number|undefined> = ref(undefined)

const singleJobsiteBucketDienstvolumenRenderset = (bucket:DemandBucketSingleJobsite) => {
    const result = {
        title: '',
        subtitle: '',
        icon: ''
    }

    const requestedMultipleMonths = !isSet(until.value) || (begin.value && (month.getKapitelMonthFromKapitelDate(begin.value) !== month.getKapitelMonthFromKapitelDate(until.value)))

    if (isSet(bucket.offeneDienstMenge)) {
        if (bucket.offeneDienstMenge === 0) {
            result.title = 'Keine offenen Dienste aktuell'
            result.subtitle = `${pluralizedBookingAmount(bucket.gesamtDienstMenge ?? 0)} insgesamt`
        } else if (requestedMultipleMonths) {
            result.icon = bucket.offeneDienstMenge.toString()
            result.title = pluralizeUnitOnly(bucket.offeneDienstMenge, 'offener Dienst', 'offene Dienste')
            if (isSet(bucket.offeneDiensteZeitraeumeWennWenige)) {
                 result.title += ' am ' + listFormat(bucket.offeneDiensteZeitraeumeWennWenige, {style: "short"}, 'en')
            } else if (bucket.offeneDiensteMonate.length > 0) {
                const months = bucket.offeneDiensteMonate as kapitelMonthString[];

                if (months.length === 1) {
                    result.title += ' im ' + month.formatKapitelMonth(months[0])
                } else if (months.length === 2) {
                    result.title += ' im ' + listFormat(months.toSorted(month.cmpKapitelMonth).map((m) => month.formatKapitelMonth(m, 'LLL.')), {style: "short"}, 'en')
                } else {
                    const firstMonth = month.min(...months)
                    result.title += ' ab ' + month.formatKapitelMonth(firstMonth)
                }


            }

            result.subtitle = `von insgesamt ${pluralizeHuman(bucket.gesamtDienstMenge ?? 0, 'einem Dienst', 'Diensten')}`
        }
    } else {
        result.title = bucket.offeneDienstMengeFuzzy ? capitalize(bucket.offeneDienstMengeFuzzy): ''
        result.subtitle = bucket.gesamtDienstMengeFuzzy ? capitalize(bucket.gesamtDienstMengeFuzzy) : ''
    }

    return result
}


const onClickButtonMoreInfo = (bucket: DemandBucketSingleJobsite) => {
    if (!bucket.jobsite) {
        return
    }

    jobsiteDemandModalStartMonth.value = bucket.offeneDiensteMonate.length > 0
        ? month.min(...bucket.offeneDiensteMonate as kapitelMonthString[])
        : (begin.value
            ? month.getKapitelMonthFromKapitelDate(begin.value)
            : undefined
        )
    jobsiteDemandModalModelValue.value = bucket.jobsite
}

const fetchDemandBuckets = async () => {
    jobsiteIds.value = props.initData.jobsiteIds ?? null
    shiftSelection.value = props.initData.shiftSelection
    begin.value = (props.initData.dateRange?.begin as kapitelDateString|undefined) ?? date.getToday()
    until.value = props.initData.dateRange?.until
    spotlight.value = props.initData.spotlight ?? 'dienstvolumen'

    if(jobsiteIds.value && jobsiteIds.value.length == 1 && jobsiteIds.value[0] == "*"){
        jobsiteIds.value = null
    }
    buckets.value = await getDemandBuckets(
        begin.value, until.value, jobsiteIds.value as number[] | null, shiftSelection.value
    )

}

const loader = new DataLoader([
    fetchDemandBuckets
])

loader.load()

</script>

<style scoped lang="scss">
    ion-chip {
        //transform: translateY(0.5em);
        flex: 0 0 auto;
    }

</style>
